/**
 * Metadata Extraction Utilities
 *
 * Functions to extract metadata from moderation cases including username,
 * segment name, user comments, and other case-specific information.
 */

import type { ModerationCase } from '@moderation/domain/ModerationCase';

/**
 * Extracts username from moderation case
 * Checks subjectOwner additionalInfo for a 'username' key, then falls back
 * to subjectOwner id and finally subjectId.
 *
 * @returns Username string or null if not found
 */
export function extractUsername(moderationCase: ModerationCase | null | undefined): string | null {
  if (!moderationCase?.report) {
    return null;
  }

  // Check subjectOwner additionalInfo for username
  const subjectOwner = moderationCase.report.subjectOwner;

  if (subjectOwner?.additionalInfo) {
    const usernameEntry = subjectOwner.additionalInfo.find(info => info.key === 'username');

    if (usernameEntry?.value) {
      return usernameEntry.value;
    }
  }

  // Fall back to subjectOwner id
  if (subjectOwner?.id) {
    return subjectOwner.id;
  }

  // Fall back to subjectId
  if (moderationCase.subjectId) {
    return moderationCase.subjectId;
  }

  return null;
}

/**
 * Extracts segment name from moderation case
 * Segment typically represents the context or category of the content
 *
 * @returns Segment name string or null if not found
 */
export function extractSegmentName(moderationCase: ModerationCase | null | undefined): string | null {
  if (!moderationCase?.report) {
    return null;
  }

  // Check custom_values for segment_name
  // @ts-expect-error - custom_values may not be in type definition yet
  const segmentName = moderationCase.report.custom_values?.segment_name;

  if (segmentName) {
    return segmentName;
  }

  // Check custom_values for segmentName (camelCase variant)
  // @ts-expect-error - custom_values may not be in type definition yet
  const segmentNameCamel = moderationCase.report.custom_values?.segmentName;

  if (segmentNameCamel) {
    return segmentNameCamel;
  }

  return null;
}

/**
 * Extracts user comment from report
 * Checks feedbackComment on the report first (from feedback_comment in API),
 * then falls back to reportSource.comment
 *
 * @returns Comment string or null if not found
 */
export function extractUserComment(moderationCase: ModerationCase | null | undefined): string | null {
  if (!moderationCase?.report) {
    return null;
  }

  // Check feedbackComment on the report (from feedback_comment in API)
  const feedbackComment = moderationCase.report.feedbackComment;

  if (feedbackComment && typeof feedbackComment === 'string') {
    return feedbackComment;
  }

  // Fall back to reportSource.comment
  const comment = moderationCase.report.reportSource?.comment;

  if (comment && typeof comment === 'string') {
    return comment;
  }

  return null;
}

/**
 * Extracts timestamp from report
 * Returns formatted date string
 *
 * @returns ISO timestamp string or null if not found
 */
export function extractTimestamp(moderationCase: ModerationCase | null | undefined): string | null {
  if (!moderationCase?.report?.timestamp) {
    return null;
  }

  return moderationCase.report.timestamp;
}

/**
 * Formats timestamp to human-readable date and time string in UTC
 *
 * @param timestamp - ISO timestamp string
 * @returns Formatted date+time string in UTC (e.g., "Jan 21, 2026, 3:45 PM")
 */
export function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) {
    return 'Unknown date';
  }

  try {
    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return 'Invalid date';
    }

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    });
  } catch {
    return 'Invalid date';
  }
}

/**
 * Formats timestamp to human-readable date and time string in UTC (minute precision)
 * Used for tooltip display to show timestamp information
 *
 * @param timestamp - ISO timestamp string
 * @returns Formatted date+time string in UTC (e.g., "Jan 21, 2026, 3:45 PM UTC")
 */
export function formatFullTimestamp(timestamp: string | null): string {
  if (!timestamp) {
    return 'Unknown date and time';
  }

  try {
    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return 'Invalid date';
    }

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
      timeZoneName: 'short',
    });
  } catch {
    return 'Invalid date';
  }
}

/**
 * Extracts subject ID (user identifier)
 *
 * @returns Subject ID or null if not found
 */
export function extractSubjectId(moderationCase: ModerationCase | null | undefined): string | null {
  return moderationCase?.subjectId || null;
}

/**
 * Extracts case ID
 *
 * @returns Case ID or null if not found
 */
export function extractCaseId(moderationCase: ModerationCase | null | undefined): string | null {
  return moderationCase?.caseId || null;
}

/**
 * Extracts community ID
 *
 * @returns Community ID or null if not found
 */
export function extractCommunityId(moderationCase: ModerationCase | null | undefined): string | null {
  return moderationCase?.communityId || null;
}

/**
 * Extracts topic name
 *
 * @returns Topic name or null if not found
 */
export function extractTopicName(moderationCase: ModerationCase | null | undefined): string | null {
  return moderationCase?.topicName || null;
}

/**
 * Extracts case status
 *
 * @returns Case status or null if not found
 */
export function extractStatus(moderationCase: ModerationCase | null | undefined): string | null {
  return moderationCase?.status || null;
}

/**
 * Gets all metadata as a structured object
 * Useful for passing multiple metadata fields at once
 */
export interface CaseMetadata {
  username: string | null;
  segmentName: string | null;
  comment: string | null;
  timestamp: string | null;
  formattedTimestamp: string;
  fullTimestamp: string;
  subjectId: string | null;
  caseId: string | null;
  communityId: string | null;
  topicName: string | null;
  status: string | null;
}

/**
 * Extracts all metadata from a moderation case
 *
 * @returns Structured metadata object
 */
export function extractAllMetadata(moderationCase: ModerationCase | null | undefined): CaseMetadata {
  const timestamp = extractTimestamp(moderationCase);

  return {
    username: extractUsername(moderationCase),
    segmentName: extractSegmentName(moderationCase),
    comment: extractUserComment(moderationCase),
    timestamp,
    formattedTimestamp: formatTimestamp(timestamp),
    fullTimestamp: formatFullTimestamp(timestamp),
    subjectId: extractSubjectId(moderationCase),
    caseId: extractCaseId(moderationCase),
    communityId: extractCommunityId(moderationCase),
    topicName: extractTopicName(moderationCase),
    status: extractStatus(moderationCase),
  };
}
